import React, { useState, useEffect, useRef } from 'react';
import '../style.css';

const API = 'https://culturlingua.com/blog_api.php';
const NAV_H = 72; // px — matches --header-height in style.css

const CATEGORY_COLORS = {
  'Inglés': '#3b6fe0', 'Consejos': '#059669', 'Certificaciones': '#7c3aed',
  'Para padres': '#ea580c', 'Empresas': '#0891b2', 'Español': '#dc2626', 'General': '#6b7280'
};

const EMOJI_GROUPS = [
  { label: 'Idiomas',       emojis: ['🌍','🗣️','💬','🗺️','🌐','📢','🔤','🔡','✏️','🖊️'] },
  { label: 'Aprendizaje',   emojis: ['📚','📖','🎓','🏫','🧠','💡','🔬','📝','📋','✅'] },
  { label: 'Inglés',        emojis: ['🇬🇧','🇺🇸','🎧','🎤','🎙️','📻','🗞️','📰','🧩','🎯'] },
  { label: 'Español',       emojis: ['🇲🇽','🇪🇸','🌮','💃','🎸','🌺','☀️','🏖️','🎭','🌻'] },
  { label: 'Comunicación',  emojis: ['💌','📧','📞','🤝','👥','🧑‍💼','💼','🏆','⭐','🎖️'] },
  { label: 'Tecnología',    emojis: ['💻','📱','🖥️','⌨️','🖱️','🎮','🤖','📡','🔧','⚙️'] },
  { label: 'Viajes',        emojis: ['✈️','🧳','🗼','🏛️','🗽','🏯','⛺','🚢','🌄','🌇'] },
  { label: 'Varios',        emojis: ['🔥','💪','🚀','🌟','💎','🎁','🎉','🎊','🌈','❤️'] },
];

function getToken() { return localStorage.getItem('blog_token'); }
function authHeaders() { return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() }; }

// ── Shared mini-components ──────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '20px', borderTop: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, color: 'var(--navy)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '6px' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: '16px' }}>{children}</div>;
}

function Panel({ children, style }) {
  return <div style={{ background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '24px', ...style }}>{children}</div>;
}

// ── Emoji Picker ────────────────────────────────────────────────
function EmojiPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const allEmojis = EMOJI_GROUPS.flatMap(g => g.emojis);
  const filtered = filter ? allEmojis.filter(e => e.includes(filter)) : null;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ width: '56px', height: '56px', borderRadius: '12px', border: `1.5px solid ${open ? '#1a1744' : '#e5e7eb'}`, background: 'white', fontSize: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value || '＋'}
        </button>
        {value && <button type="button" onClick={() => onChange('')} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '0.8rem' }}>Quitar</button>}
        <span style={{ color: '#999', fontSize: '0.8rem' }}>{value ? 'Emoji seleccionado' : 'Sin emoji — se usará color + iniciales'}</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '64px', left: 0, zIndex: 100, background: 'white', borderRadius: '16px', boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', width: '360px', padding: '16px' }}>
          <input autoFocus placeholder="Buscar emoji…" value={filter} onChange={e => setFilter(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }} />
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {(filtered ? [{ label: 'Resultados', emojis: filtered }] : EMOJI_GROUPS).map(group => (
              <div key={group.label} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: '#bbb', marginBottom: '6px' }}>{group.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {group.emojis.map(e => (
                    <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); setFilter(''); }}
                      style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', background: value === e ? '#f0f0ff' : 'transparent', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={ev => ev.currentTarget.style.background = value === e ? '#f0f0ff' : 'transparent'}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Login ───────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [err,  setErr]  = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true); setErr('');
    const r = await fetch(`${API}?action=login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user, pass }) });
    const d = await r.json();
    setLoading(false);
    if (d.token) { localStorage.setItem('blog_token', d.token); onLogin(); }
    else setErr('Usuario o contraseña incorrectos');
  };
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '48px', width: '360px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h2 style={{ fontFamily: 'Fraunces, serif', color: 'var(--navy)', marginBottom: '8px', fontSize: '1.6rem' }}>Blog Admin</h2>
        <p style={{ color: '#999', fontSize: '0.875rem', marginBottom: '32px' }}>Culturlingua</p>
        <input placeholder="Usuario" value={user} onChange={e => setUser(e.target.value)} style={inputStyle} />
        <input type="password" placeholder="Contraseña" value={pass} onChange={e => setPass(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} style={{ ...inputStyle, marginTop: '12px' }} />
        {err && <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '8px' }}>{err}</p>}
        <button onClick={submit} disabled={loading}
          style={{ width: '100%', padding: '13px', marginTop: '20px', borderRadius: '10px', background: 'var(--navy)', color: 'white', border: 'none', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', cursor: 'pointer' }}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e5e7eb', fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', display: 'block' };
const labelStyle = { display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, color: 'var(--navy)', fontSize: '0.85rem', marginBottom: '6px' };

// ── Editor ──────────────────────────────────────────────────────
function Editor({ post, onSave, onCancel }) {
  const isNew = !post?.id;
  const [form, setForm] = useState({
    slug: post?.slug || '', title: post?.title || '', excerpt: post?.excerpt || '',
    content: post?.content || '', category: post?.category || 'Consejos',
    category_color: post?.category_color || '#3b6fe0', icon: post?.icon || '',
    read_time: post?.read_time || '4 min', published: post?.published ?? 1,
    faq_json: post?.faq_json || '',
    og_image: post?.og_image || '',
  });
  const [faqs, setFaqs] = useState(() => {
    try { return post?.faq_json ? JSON.parse(post.faq_json) : []; } catch { return []; }
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoSlug = (t) => t.toLowerCase()
    .replace(/[áàä]/g,'a').replace(/[éèë]/g,'e').replace(/[íìï]/g,'i').replace(/[óòö]/g,'o').replace(/[úùü]/g,'u').replace(/ñ/g,'n')
    .replace(/[^a-z0-9\s]/g,'').replace(/\s+/g,'-').slice(0,80);
  const save = async () => {
    setSaving(true); setErr('');
    // sync faqs array → faq_json string before save
    const validFaqs = faqs.filter(f => f.q.trim() && f.a.trim());
    const payload = { ...form, faq_json: validFaqs.length ? JSON.stringify(validFaqs) : '' };
    const url = isNew ? `${API}?action=create` : `${API}?action=update&id=${post.id}`;
    const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
    const d = await r.json();
    setSaving(false);
    if (d.success) onSave(); else setErr(d.error || 'Error al guardar');
  };
  const addFaq    = () => setFaqs(f => [...f, { q: '', a: '' }]);
  const removeFaq = (i) => setFaqs(f => f.filter((_, idx) => idx !== i));
  const updateFaq = (i, field, val) => setFaqs(f => f.map((item, idx) => idx === i ? { ...item, [field]: val } : item));
  const previewColor = form.category_color || '#3b6fe0';
  return (
    <div style={{ maxWidth: '920px', margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <h2 style={{ fontFamily: 'Fraunces, serif', color: 'var(--navy)', fontSize: '1.5rem' }}>{isNew ? 'Nuevo artículo' : 'Editar artículo'}</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.9rem' }}>← Volver</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '32px', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Título</label>
            <input value={form.title} style={inputStyle} onChange={e => { set('title', e.target.value); if (isNew) set('slug', autoSlug(e.target.value)); }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Slug (URL)</label>
              <input value={form.slug} style={inputStyle} onChange={e => set('slug', e.target.value)} />
              <p style={{ color: '#999', fontSize: '0.75rem', marginTop: '4px' }}>/blog/<strong>{form.slug || 'tu-slug-aqui'}</strong></p>
            </div>
            <div>
              <label style={labelStyle}>Tiempo de lectura</label>
              <input value={form.read_time} style={inputStyle} onChange={e => set('read_time', e.target.value)} placeholder="4 min" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Categoría</label>
              <select value={form.category} style={inputStyle}
                onChange={e => { set('category', e.target.value); set('category_color', CATEGORY_COLORS[e.target.value] || '#3b6fe0'); }}>
                {Object.keys(CATEGORY_COLORS).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Color categoría</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={form.category_color} onChange={e => set('category_color', e.target.value)}
                  style={{ width: '48px', height: '40px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }} />
                <input value={form.category_color} style={{ ...inputStyle, flex: 1 }} onChange={e => set('category_color', e.target.value)} />
              </div>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Icono del artículo</label>
            <EmojiPicker value={form.icon} onChange={v => set('icon', v)} />
          </div>
          <div>
            <label style={labelStyle}>Descripción corta (aparece en Google y en la lista)</label>
            <textarea value={form.excerpt} style={{ ...inputStyle, height: '80px', resize: 'vertical' }} onChange={e => set('excerpt', e.target.value)} />
            <p style={{ color: form.excerpt.length > 160 ? '#dc2626' : '#999', fontSize: '0.75rem', marginTop: '4px' }}>{form.excerpt.length}/160 caracteres</p>
          </div>
          <div>
            <label style={labelStyle}>Imagen OG (para Google Discover y redes sociales)</label>
            <input value={form.og_image} style={inputStyle}
              placeholder="https://culturlingua.com/images/mi-articulo.jpg (1200×630px recomendado)"
              onChange={e => set('og_image', e.target.value)} />
            <p style={{ color: '#999', fontSize: '0.75rem', marginTop: '4px' }}>
              Si dejas vacío se usa el logo del sitio. Para Google Discover necesitas una imagen ≥ 1200px de ancho.
            </p>
            {form.og_image && (
              <img src={form.og_image} alt="OG preview"
                style={{ marginTop: '8px', maxWidth: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                onError={e => e.currentTarget.style.display = 'none'} />
            )}
          </div>
          <div>
            <label style={labelStyle}>Contenido HTML</label>
            <p style={{ color: '#999', fontSize: '0.75rem', marginBottom: '8px' }}>Usa: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;ol&gt;&lt;li&gt;, &lt;strong&gt;, &lt;blockquote&gt;, &lt;a href=""&gt;</p>
            <textarea value={form.content} style={{ ...inputStyle, height: '400px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.82rem' }} onChange={e => set('content', e.target.value)} />
          </div>

          {/* ── FAQ editor ── */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={labelStyle}>Preguntas frecuentes (FAQ)</label>
              <span style={{ fontSize: '0.72rem', color: '#999' }}>Aparecen al final del artículo y en Google como fragmentos</span>
            </div>
            {faqs.length === 0 && (
              <div style={{ background: '#fafaf9', border: '1.5px dashed #e5e0d8', borderRadius: '10px', padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '0.85rem', marginBottom: '8px' }}>
                Sin preguntas aún — haz clic en "+ Agregar pregunta" para empezar
              </div>
            )}
            {faqs.map((faq, i) => (
              <div key={i} style={{ background: '#fafaf9', border: '1.5px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '10px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '10px', right: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#ccc', textTransform: 'uppercase', letterSpacing: '.08em' }}>FAQ {i + 1}</span>
                  <button type="button" onClick={() => removeFaq(i)}
                    style={{ background: 'none', border: 'none', color: '#e0a0a0', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '2px 4px', borderRadius: '4px' }}
                    title="Eliminar pregunta">✕</button>
                </div>
                <div style={{ marginBottom: '10px', paddingRight: '64px' }}>
                  <label style={{ ...labelStyle, fontSize: '0.78rem', color: '#888', marginBottom: '4px' }}>Pregunta</label>
                  <input value={faq.q} placeholder="Ej: ¿Cuánto tiempo lleva aprender inglés?"
                    style={{ ...inputStyle, fontSize: '0.875rem' }}
                    onChange={e => updateFaq(i, 'q', e.target.value)} />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.78rem', color: '#888', marginBottom: '4px' }}>Respuesta</label>
                  <textarea value={faq.a} placeholder="Respuesta completa y clara..."
                    style={{ ...inputStyle, height: '72px', resize: 'vertical', fontSize: '0.875rem' }}
                    onChange={e => updateFaq(i, 'a', e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" onClick={addFaq}
              style={{ padding: '9px 18px', borderRadius: '8px', border: '1.5px dashed #c5bfb5', background: 'white', color: '#888', fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', width: '100%', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#c5bfb5'; e.currentTarget.style.color = '#888'; }}>
              + Agregar pregunta
            </button>
            {faqs.length > 0 && (
              <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '6px' }}>
                {faqs.filter(f => f.q.trim() && f.a.trim()).length} de {faqs.length} preguntas completas · Solo las completas se guardan y aparecen en Google
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.published === 1} onChange={e => set('published', e.target.checked ? 1 : 0)} style={{ width: '16px', height: '16px' }} />
              <span style={{ fontFamily: 'DM Sans', fontWeight: 600, color: 'var(--navy)' }}>Publicado</span>
            </label>
            <span style={{ color: '#999', fontSize: '0.82rem' }}>{form.published ? 'Visible en el blog público' : 'Borrador — no visible aún'}</span>
          </div>
          {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', color: '#dc2626', fontSize: '0.875rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '13px 32px', borderRadius: '10px', background: 'var(--navy)', color: 'white', border: 'none', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', fontSize: '0.95rem' }}>
              {saving ? 'Guardando...' : (isNew ? 'Publicar artículo' : 'Guardar cambios')}
            </button>
            <button onClick={onCancel} style={{ padding: '13px 24px', borderRadius: '10px', background: '#f3f4f6', color: '#666', border: 'none', fontWeight: 600, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
        {/* Live preview */}
        <div style={{ position: 'sticky', top: NAV_H + 16 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: '#bbb', marginBottom: '12px' }}>Vista previa</div>
          <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(26,23,68,.10)' }}>
            <div style={{ height: '140px', background: `linear-gradient(135deg, ${previewColor}, ${previewColor}99)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position:'absolute',inset:0, backgroundImage:`radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)`, backgroundSize:'20px 20px' }}/>
              {form.icon
                ? <span style={{ fontSize: '52px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.2))', position: 'relative', zIndex: 2 }}>{form.icon}</span>
                : <span style={{ fontFamily: 'Fraunces, serif', fontSize: '3.5rem', fontWeight: 900, color: 'rgba(255,255,255,0.2)', position: 'relative', zIndex: 2 }}>{(form.category||'Ge').substring(0,2).toUpperCase()}</span>
              }
            </div>
            <div style={{ padding: '18px 20px 16px' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', padding: '3px 9px', borderRadius: '100px', background: previewColor+'18', color: previewColor, marginBottom: '10px', display: 'inline-block' }}>{form.category||'Categoría'}</span>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', fontWeight: 800, color: '#1a1744', lineHeight: 1.3, marginBottom: '8px' }}>{form.title||'Título del artículo'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #f0ede8', fontSize: '0.76rem' }}>
                <span style={{ color: '#c0b8a8' }}>Hoy</span>
                <span style={{ color: '#3b6fe0', fontWeight: 700 }}>{form.read_time||'4 min'} →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Analytics tab ───────────────────────────────────────────────
// ── GeoMap — Mexico states SVG map with city dots ──────────────
const GEO_LNG_MIN=-125, GEO_LNG_MAX=-84, GEO_LAT_MIN=12, GEO_LAT_MAX=42;
const MAP_W=480, MAP_H=280;
function projectGeo(lat,lng){
  const x=((lng-GEO_LNG_MIN)/(GEO_LNG_MAX-GEO_LNG_MIN))*MAP_W;
  const y=MAP_H-((lat-GEO_LAT_MIN)/(GEO_LAT_MAX-GEO_LAT_MIN))*MAP_H;
  return [Math.round(x*10)/10, Math.round(y*10)/10];
}
function isMexico(cc){ return !cc||cc==='MX'; }

const MX_STATES = {
  "Baja California": "M 92.3 88.4 L 96.9 86.6 L 120.4 86.6 L 122.1 92.1 L 128.3 93.6 L 138.8 103.7 L 150.2 112.3 L 159.6 122.0 L 167.9 132.5 L 176.4 140.7 L 182.4 172.9 L 157.2 186.4 L 150.9 191.4 L 103.3 215.8 L 97.8 219.9 L 92.3 88.4 Z",
  "Sonora":          "M 120.4 86.6 L 154.3 99.6 L 163.0 99.6 L 170.3 99.6 L 196.6 99.6 L 196.6 95.4 L 215.1 94.3 L 193.2 112.0 L 175.6 144.7 L 167.9 132.5 L 159.6 122.0 L 150.2 112.3 L 138.8 103.7 L 120.4 86.6 Z",
  "Chihuahua":       "M 196.6 99.6 L 215.1 94.3 L 233.8 105.9 L 237.9 112.6 L 253.8 121.7 L 245.9 135.3 L 234.1 144.7 L 222.4 149.3 L 210.7 149.3 L 199.0 140.0 L 193.2 121.3 L 193.2 112.0 L 196.6 99.6 Z",
  "Coahuila":        "M 253.8 121.7 L 263.6 114.1 L 273.2 114.1 L 281.4 117.8 L 298.3 134.8 L 292.7 144.7 L 281.0 149.3 L 269.3 149.3 L 257.6 144.7 L 245.9 135.3 L 253.8 121.7 Z",
  "Nuevo León":      "M 281.4 117.8 L 315.2 148.8 L 310.2 158.7 L 304.4 168.0 L 298.5 168.0 L 292.7 158.7 L 281.0 149.3 L 292.7 144.7 L 281.4 117.8 Z",
  "Tamaulipas":      "M 315.2 148.8 L 326.2 149.6 L 322.9 184.6 L 317.6 199.5 L 310.2 205.3 L 298.5 186.7 L 298.5 168.0 L 304.4 168.0 L 310.2 158.7 L 315.2 148.8 Z",
  "Sinaloa":         "M 175.6 144.7 L 193.2 121.3 L 199.0 140.0 L 210.7 149.3 L 210.7 168.0 L 231.2 199.8 L 222.4 196.0 L 210.7 186.7 L 199.0 177.3 L 193.2 168.0 L 182.2 152.2 L 175.6 144.7 Z",
  "Durango":         "M 210.7 149.3 L 222.4 149.3 L 234.1 144.7 L 245.9 135.3 L 257.6 144.7 L 269.3 149.3 L 275.1 158.7 L 257.6 168.0 L 245.9 177.3 L 234.1 182.0 L 222.4 177.3 L 210.7 168.0 L 210.7 149.3 Z",
  "Zacatecas":       "M 269.3 149.3 L 286.8 149.3 L 292.7 158.7 L 298.5 168.0 L 298.5 186.7 L 281.0 191.3 L 269.3 186.7 L 257.6 182.0 L 257.6 168.0 L 275.1 158.7 L 269.3 149.3 Z",
  "San Luis Potosí": "M 298.5 168.0 L 310.2 186.7 L 304.4 196.0 L 298.5 200.7 L 292.7 205.3 L 286.8 200.7 L 281.0 191.3 L 298.5 186.7 L 298.5 168.0 Z",
  "Nayarit":         "M 222.4 177.3 L 234.1 182.0 L 240.0 191.3 L 234.1 196.0 L 231.2 199.8 L 222.4 196.0 L 222.4 177.3 Z",
  "Jalisco":         "M 234.1 182.0 L 245.9 177.3 L 257.6 182.0 L 269.3 186.7 L 281.0 191.3 L 275.1 205.3 L 257.6 210.0 L 251.7 205.3 L 240.0 191.3 L 234.1 182.0 Z",
  "Aguascalientes":  "M 269.3 186.7 L 281.0 191.3 L 278.0 188.0 L 272.0 185.0 L 269.3 186.7 Z",
  "Guanajuato":      "M 281.0 191.3 L 292.7 186.7 L 298.5 186.7 L 304.4 196.0 L 298.5 200.7 L 292.7 205.3 L 286.8 200.7 L 281.0 196.0 L 281.0 191.3 Z",
  "Querétaro":       "M 298.5 200.7 L 304.4 196.0 L 310.2 205.3 L 304.4 205.3 L 298.5 200.7 Z",
  "Hidalgo":         "M 304.4 196.0 L 316.1 196.0 L 310.2 205.3 L 304.4 205.3 L 304.4 196.0 Z",
  "Colima":          "M 245.9 207.0 L 251.7 205.3 L 257.6 210.0 L 251.7 212.0 L 245.9 207.0 Z",
  "Michoacán":       "M 251.7 205.3 L 257.6 210.0 L 269.3 214.7 L 275.1 205.3 L 281.0 214.7 L 275.1 224.0 L 263.4 219.3 L 257.6 214.7 L 251.7 205.3 Z",
  "México (estado)": "M 286.8 200.7 L 292.7 205.3 L 304.4 210.0 L 298.5 214.7 L 292.7 210.0 L 286.8 205.3 L 286.8 200.7 Z",
  "CDMX":            "M 300.9 209.1 L 304.4 210.0 L 305.0 211.0 L 302.0 212.0 L 300.9 209.1 Z",
  "Tlaxcala":        "M 310.2 210.0 L 313.8 210.0 L 313.0 213.0 L 310.2 210.0 Z",
  "Puebla":          "M 304.4 205.3 L 316.1 205.3 L 322.0 205.3 L 327.8 214.7 L 322.0 219.3 L 316.1 214.7 L 310.2 210.0 L 304.4 210.0 L 304.4 205.3 Z",
  "Morelos":         "M 300.9 209.1 L 304.4 210.0 L 304.4 214.7 L 300.0 214.0 L 300.9 209.1 Z",
  "Guerrero":        "M 263.4 219.3 L 275.1 224.0 L 286.8 219.3 L 296.4 236.0 L 305.0 233.3 L 305.0 224.0 L 305.0 214.7 L 298.5 214.7 L 292.7 214.7 L 281.0 214.7 L 263.4 219.3 Z",
  "Veracruz":        "M 322.9 184.6 L 326.2 149.6 L 315.2 148.8 L 310.2 158.7 L 310.2 186.7 L 316.1 196.0 L 316.1 205.3 L 317.6 199.5 L 322.9 184.6 Z",
  "Oaxaca":          "M 322.0 219.3 L 327.8 214.7 L 341.5 216.1 L 334.9 214.0 L 331.3 211.5 L 322.0 214.7 L 322.0 219.3 Z",
  "Chiapas":         "M 341.5 216.1 L 354.4 219.7 L 362.9 228.7 L 368.8 224.0 L 380.5 233.3 L 386.3 238.0 L 389.5 242.0 L 355.0 245.9 L 340.1 242.9 L 341.5 216.1 Z",
  "Tabasco":         "M 354.4 219.7 L 362.9 219.3 L 374.6 214.7 L 380.5 219.3 L 374.6 228.7 L 362.9 228.7 L 354.4 219.7 Z",
  "Campeche":        "M 380.5 219.3 L 403.6 219.8 L 419.7 225.7 L 419.7 242.7 L 389.5 242.0 L 380.5 233.3 L 380.5 219.3 Z",
  "Yucatán":         "M 403.6 219.8 L 438.8 219.8 L 438.8 191.6 L 409.8 196.0 L 403.6 219.8 Z",
  "Quintana Roo":    "M 438.8 219.8 L 448.2 193.9 L 439.4 209.3 L 435.0 225.7 L 438.8 219.8 Z",
};

function GeoMap({ cities }) {
  const [tooltip, setTooltip] = React.useState(null);
  const [hoverState, setHoverState] = React.useState(null);

  if (!cities.length) return (
    <div style={{ height:'220px', display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:'0.85rem', background:'#f8faff', borderRadius:'10px', border:'1px solid #e5e7eb' }}>
      Sin datos de coordenadas aún
    </div>
  );

  const maxCnt = Math.max(...cities.map(c => parseInt(c.cnt)||1));

  return (
    <div style={{ position:'relative' }}>
      <svg width="100%" viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        style={{ background:'#ddeeff', borderRadius:'10px', border:'1px solid #c5d8ef', display:'block' }}>

        {/* Ocean */}
        <rect width={MAP_W} height={MAP_H} fill="#ddeeff" rx="10"/>

        {/* US label */}
        <text x="220" y="22" textAnchor="middle" fill="#a0b8d8" fontSize="9" fontFamily="DM Sans,sans-serif">Estados Unidos</text>

        {/* Mexico states */}
        {Object.entries(MX_STATES).map(([name, path]) => (
          <path key={name} d={path}
            fill={hoverState===name ? '#7eb3e8' : '#c8daf5'}
            stroke="white" strokeWidth="0.8" strokeLinejoin="round"
            style={{ cursor:'default', transition:'fill .15s' }}
            onMouseEnter={() => setHoverState(name)}
            onMouseLeave={() => setHoverState(null)}
          />
        ))}

        {/* Ocean labels */}
        <text x="90"  y="220" textAnchor="middle" fill="#8ab0cc" fontSize="8" fontFamily="DM Sans,sans-serif">Océano Pacífico</text>
        <text x="310" y="145" textAnchor="middle" fill="#8ab0cc" fontSize="8" fontFamily="DM Sans,sans-serif">Golfo de México</text>

        {/* State hover label */}
        {hoverState && (
          <text x={MAP_W/2} y={MAP_H-8} textAnchor="middle" fill="#1a1744" fontSize="9" fontWeight="700" fontFamily="DM Sans,sans-serif" opacity="0.7">
            {hoverState}
          </text>
        )}

        {/* City dots — always on top */}
        {cities.map((city, i) => {
          const lat = parseFloat(city.lat);
          const lng = parseFloat(city.lng);
          if (isNaN(lat) || isNaN(lng) || lat===0 || lng===0) return null;
          const [x, y] = projectGeo(lat, lng);
          if (x<2 || x>MAP_W-2 || y<2 || y>MAP_H-2) return null;
          const cnt   = parseInt(city.cnt)||1;
          const r     = Math.max(7, Math.min(22, 7+(cnt/maxCnt)*15));
          const color = isMexico(city.country_code) ? '#1a1744' : '#f59e0b';
          const opacity = 0.55+(cnt/maxCnt)*0.35;
          return (
            <g key={i} style={{ cursor:'default' }}
              onMouseEnter={() => setTooltip({city:city.city, country:city.country_code, cnt, x, y})}
              onMouseLeave={() => setTooltip(null)}>
              <circle cx={x} cy={y} r={r+5} fill={color} opacity={0.15}/>
              <circle cx={x} cy={y} r={r}   fill={color} opacity={opacity} stroke="white" strokeWidth="2"/>
              <circle cx={x} cy={y} r={Math.max(3,r*0.35)} fill="white" opacity={0.8}/>
            </g>
          );
        })}

        {/* Tooltip */}
        {tooltip && (() => {
          const tw=120, th=38;
          const tx = tooltip.x+tw > MAP_W-8 ? tooltip.x-tw-8 : tooltip.x+12;
          const ty = tooltip.y+th > MAP_H-8 ? tooltip.y-th-4 : tooltip.y+8;
          return (
            <g pointerEvents="none">
              <rect x={tx-4} y={ty-16} width={tw} height={th} rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1"/>
              <text x={tx+tw/2-4} y={ty-2}  textAnchor="middle" fill="#1a1744" fontSize="10.5" fontWeight="700" fontFamily="DM Sans,sans-serif">{tooltip.city}</text>
              <text x={tx+tw/2-4} y={ty+13} textAnchor="middle" fill="#94a3b8" fontSize="9"    fontFamily="DM Sans,sans-serif">{tooltip.country||'MX'} · {tooltip.cnt} visita{tooltip.cnt!==1?'s':''}</text>
            </g>
          );
        })()}
      </svg>

      {/* City pills */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginTop:'10px' }}>
        {cities.slice(0,12).map((c,i) => (
          <span key={i} style={{ background:isMexico(c.country_code)?'rgba(26,23,68,.06)':'rgba(245,158,11,.08)', border:`1px solid ${isMexico(c.country_code)?'rgba(26,23,68,.18)':'rgba(245,158,11,.25)'}`, borderRadius:'100px', padding:'3px 9px', fontSize:'0.72rem', color:'#555', display:'flex', alignItems:'center', gap:'4px' }}>
            <span style={{ width:'6px', height:'6px', borderRadius:'50%', flexShrink:0, background:isMexico(c.country_code)?'#1a1744':'#f59e0b', display:'inline-block' }}/>
            {c.city} <span style={{ color:'#aaa' }}>({c.cnt})</span>
          </span>
        ))}
      </div>
    </div>
  );
}


// ── Chart.js loader ───────────────────────────────────────────────
function useCDNChart() {
  const [ready, setReady] = React.useState(!!window.Chart);
  React.useEffect(() => {
    if (window.Chart) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
}

// ── Trend Chart — daily sessions + organic (last 30 days) ─────────
function TrendChart({ timeline }) {
  const canvasRef = React.useRef(null);
  const chartRef  = React.useRef(null);
  const chartReady = useCDNChart();

  React.useEffect(() => {
    if (!chartReady || !canvasRef.current || !timeline?.length) return;
    if (chartRef.current) { chartRef.current.destroy(); }

    const labels   = timeline.map(r => {
      const d = new Date(r.date + 'T12:00:00');
      return d.toLocaleDateString('es-MX', { month:'short', day:'numeric' });
    });
    const sessions = timeline.map(r => parseInt(r.sessions)||0);
    const organic  = timeline.map(r => parseInt(r.organic)||0);

    chartRef.current = new window.Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Sesiones totales',
            data: sessions,
            borderColor: '#3b6fe0',
            backgroundColor: 'rgba(59,111,224,0.08)',
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#3b6fe0',
            fill: true,
            tension: 0.4,
          },
          {
            label: 'Orgánico',
            data: organic,
            borderColor: '#059669',
            backgroundColor: 'rgba(5,150,105,0.06)',
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#059669',
            fill: true,
            tension: 0.4,
            borderDash: [4,3],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode:'index', intersect:false },
        plugins: {
          legend: {
            position: 'top',
            labels: { font:{ family:'DM Sans,sans-serif', size:12 }, padding:16, usePointStyle:true }
          },
          tooltip: {
            backgroundColor: '#1a1744',
            titleFont: { family:'Fraunces,serif', size:13 },
            bodyFont:  { family:'DM Sans,sans-serif', size:12 },
            padding: 12,
            cornerRadius: 10,
          }
        },
        scales: {
          x: {
            grid: { color:'rgba(0,0,0,0.04)' },
            ticks: { font:{ family:'DM Sans,sans-serif', size:11 }, maxRotation:45 }
          },
          y: {
            beginAtZero: true,
            grid: { color:'rgba(0,0,0,0.04)' },
            ticks: { font:{ family:'DM Sans,sans-serif', size:11 }, stepSize:1 }
          }
        }
      }
    });
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [chartReady, timeline]);

  if (!timeline?.length) return (
    <p style={{ color:'#999', fontSize:'0.85rem', textAlign:'center', padding:'24px' }}>
      Sin datos de tendencia aún — aparecerá cuando haya visitas registradas.
    </p>
  );

  // Calculate trend
  const trend = timeline.length >= 2 ? (() => {
    const first = parseInt(timeline[0]?.sessions)||0;
    const last  = parseInt(timeline[timeline.length-1]?.sessions)||0;
    const mid   = Math.floor(timeline.length / 2);
    const firstHalf = timeline.slice(0, mid).reduce((s,r)=>s+(parseInt(r.sessions)||0),0);
    const secondHalf= timeline.slice(mid).reduce((s,r)=>s+(parseInt(r.sessions)||0),0);
    return secondHalf >= firstHalf ? 'up' : 'down';
  })() : 'flat';

  const trendLabel = trend==='up' ? '↑ Tendencia al alza' : trend==='down' ? '↓ Tendencia a la baja' : '→ Estable';
  const trendColor = trend==='up' ? '#059669' : trend==='down' ? '#dc2626' : '#888';
  const totalSessions = timeline.reduce((s,r)=>s+(parseInt(r.sessions)||0),0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <div style={{ fontSize:'0.78rem', color:'#888' }}>Últimos 30 días · {totalSessions} sesiones</div>
        <span style={{ fontSize:'0.78rem', fontWeight:700, color:trendColor }}>{trendLabel}</span>
      </div>
      <div style={{ height:'200px', position:'relative' }}>
        <canvas ref={canvasRef}/>
      </div>
    </div>
  );
}

// ── Export buttons — PDF report + JSON copy ───────────────────────
function ExportButtons({ stats, funnel, concurrent }) {
  const [copied, setCopied] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);

  const buildSnapshot = () => ({
    generated_at: new Date().toISOString(),
    summary: {
      total_views:    stats?.total || 0,
      total_sessions: stats?.engagement?.total_sessions || 0,
      bounce_rate:    stats?.engagement?.total_sessions > 0
        ? Math.round((stats.engagement.total_bounced / stats.engagement.total_sessions)*100) + '%' : '0%',
      avg_scroll:     Math.round(stats?.engagement?.avg_scroll || 0) + '%',
      avg_time_min:   Math.round((stats?.engagement?.avg_time || 0)/60*10)/10,
      cta_clicks:     stats?.engagement?.total_cta_clicks || 0,
      wa_clicks:      stats?.engagement?.total_wa_clicks || 0,
      wa_leads:       stats?.engagement?.total_wa_leads || 0,
    },
    funnel: {
      total_completions: funnel?.total_completions || 0,
      from_blog:         funnel?.from_blog || 0,
      conversion_rate:   funnel?.conversion_rate || 0,
    },
    concurrent_now: concurrent,
    traffic_sources: stats?.source_breakdown || [],
    top_countries:   (stats?.geo_countries || []).slice(0,5),
    top_cities:      (stats?.geo_cities || []).slice(0,5),
    ab_test:         stats?.ab_results || [],
    top_posts:       (stats?.posts || []).slice(0,5).map(p=>({
      title:        p.title,
      views:        p.views,
      views_organic:p.views_organic||0,
      avg_scroll:   p.avg_scroll,
      cta_clicks:   p.cta_clicks||0,
      wa_leads:     p.wa_leads||0,
    })),
    timeline:        stats?.timeline || [],
  });

  const copyJSON = () => {
    const json = JSON.stringify(buildSnapshot(), null, 2);
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = json; document.body.appendChild(ta);
      ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const exportPDF = () => {
    setPdfLoading(true);
    const snap = buildSnapshot();
    const date = new Date().toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' });

    const topPostsRows = snap.top_posts.map(p => `
      <tr>
        <td>${p.title}</td>
        <td style="text-align:center">${p.views}</td>
        <td style="text-align:center">${p.views_organic}</td>
        <td style="text-align:center">${p.avg_scroll || 0}%</td>
        <td style="text-align:center">${p.cta_clicks}</td>
      </tr>`).join('');

    const sourcesRows = snap.traffic_sources.map(s => `
      <tr><td>${s.source}</td><td style="text-align:right">${s.cnt}</td></tr>`).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
    <title>Reporte Blog Culturlingua — ${date}</title>
    <style>
      body { font-family: 'Helvetica Neue', sans-serif; color: #1a1744; margin: 0; padding: 40px; font-size: 13px; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      .subtitle { color: #888; font-size: 12px; margin-bottom: 32px; }
      .grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 28px; }
      .card { background: #f4f1eb; border-radius: 10px; padding: 14px 16px; }
      .card-val { font-size: 22px; font-weight: 900; color: #1a1744; }
      .card-lbl { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
      h2 { font-size: 14px; border-bottom: 2px solid #f4f1eb; padding-bottom: 6px; margin: 24px 0 12px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      th { background: #1a1744; color: white; padding: 8px 10px; font-size: 11px; text-align: left; }
      td { padding: 7px 10px; border-bottom: 1px solid #f0ece4; font-size: 12px; }
      .funnel-row { display: flex; gap: 20px; margin-bottom: 24px; }
      .funnel-item { flex: 1; background: #f4f1eb; border-radius: 10px; padding: 14px; text-align: center; }
      .funnel-val { font-size: 20px; font-weight: 900; }
      .funnel-lbl { font-size: 10px; color: #888; margin-top: 3px; }
      .footer { margin-top: 40px; color: #aaa; font-size: 10px; text-align: center; }
      @media print { body { padding: 20px; } }
    </style></head><body>
    <h1>Reporte Blog · Culturlingua</h1>
    <div class="subtitle">Generado el ${date} · Últimos 30 días</div>

    <div class="grid">
      <div class="card"><div class="card-val">${snap.summary.total_views}</div><div class="card-lbl">Vistas totales</div></div>
      <div class="card"><div class="card-val">${snap.summary.total_sessions}</div><div class="card-lbl">Sesiones únicas</div></div>
      <div class="card"><div class="card-val">${snap.summary.avg_scroll}</div><div class="card-lbl">Scroll promedio</div></div>
      <div class="card"><div class="card-val">${snap.summary.bounce_rate}</div><div class="card-lbl">Tasa de rebote</div></div>
      <div class="card"><div class="card-val">${snap.summary.wa_clicks}</div><div class="card-lbl">WA clicks</div></div>
      <div class="card"><div class="card-val">${snap.summary.wa_leads}</div><div class="card-lbl">WA leads</div></div>
      <div class="card"><div class="card-val">${snap.summary.cta_clicks}</div><div class="card-lbl">CTA clicks</div></div>
      <div class="card"><div class="card-val">${snap.summary.avg_time_min} min</div><div class="card-lbl">Tiempo activo</div></div>
    </div>

    <h2>Funnel Blog → Examen → Lead</h2>
    <div class="funnel-row">
      <div class="funnel-item"><div class="funnel-val">${snap.summary.total_sessions}</div><div class="funnel-lbl">Sesiones blog</div></div>
      <div class="funnel-item"><div class="funnel-val">${snap.funnel.from_blog}</div><div class="funnel-lbl">Llegaron al examen</div></div>
      <div class="funnel-item"><div class="funnel-val">${snap.funnel.total_completions}</div><div class="funnel-lbl">Completaron examen</div></div>
      <div class="funnel-item"><div class="funnel-val">${snap.funnel.conversion_rate}%</div><div class="funnel-lbl">Conversión</div></div>
    </div>

    <h2>Rendimiento por artículo</h2>
    <table>
      <thead><tr><th>Artículo</th><th>Vistas</th><th>Orgánico</th><th>Scroll</th><th>CTA clicks</th></tr></thead>
      <tbody>${topPostsRows}</tbody>
    </table>

    <h2>Fuentes de tráfico</h2>
    <table>
      <thead><tr><th>Fuente</th><th>Sesiones</th></tr></thead>
      <tbody>${sourcesRows}</tbody>
    </table>

    <h2>Top países</h2>
    <table>
      <thead><tr><th>País</th><th>Sesiones</th></tr></thead>
      <tbody>${snap.top_countries.map(g=>`<tr><td>${g.country_name} (${g.country_code})</td><td style="text-align:right">${g.cnt}</td></tr>`).join('')}</tbody>
    </table>

    <div class="footer">Culturlingua Blog Analytics · culturlingua.com · ${date}</div>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); setPdfLoading(false); }, 600);
  };

  return (
    <div style={{ display:'flex', gap:'10px', marginBottom:'20px', justifyContent:'flex-end' }}>
      <button onClick={copyJSON}
        style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'9px 16px', borderRadius:'10px', border:'1.5px solid var(--border)', background: copied ? '#f0fdf4' : 'white', color: copied ? '#166534' : 'var(--text)', cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontSize:'0.82rem', fontWeight:700, transition:'all .2s' }}>
        {copied ? '✓ JSON copiado' : '{ } Copiar JSON'}
      </button>
      <button onClick={exportPDF} disabled={pdfLoading}
        style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'9px 16px', borderRadius:'10px', border:'none', background:'var(--navy)', color:'white', cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontSize:'0.82rem', fontWeight:700, opacity: pdfLoading ? 0.6 : 1, transition:'all .2s' }}>
        {pdfLoading ? '⏳ Generando...' : '📄 Exportar PDF'}
      </button>
    </div>
  );
}


function AnalyticsTab({ stats, funnel, concurrent }) {
  if (!stats) return <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>Cargando estadísticas...</p>;

  const eng      = stats.engagement || {};
  const total    = stats.total || 0;
  const sessions = eng.total_sessions || 0;
  const bounced  = eng.total_bounced  || 0;
  const bounceRate = sessions > 0 ? Math.round((bounced / sessions) * 100) : 0;
  const ctrRate    = sessions > 0 ? ((eng.total_cta_clicks || 0) / sessions * 100).toFixed(1) : '0';
  const waRate     = sessions > 0 ? ((eng.total_wa_clicks  || 0) / sessions * 100).toFixed(1) : '0';

  // Device
  const devTotal = (eng.mobile_count||0) + (eng.tablet_count||0) + (eng.desktop_count||0) || 1;
  const devPct   = {
    mobile:  Math.round((eng.mobile_count||0)/devTotal*100),
    tablet:  Math.round((eng.tablet_count||0)/devTotal*100),
    desktop: Math.round((eng.desktop_count||0)/devTotal*100),
  };

  // Scroll buckets
  const sb = stats.scroll_buckets || {};
  const sbTotal = (parseInt(sb.s0)||0)+(parseInt(sb.s25)||0)+(parseInt(sb.s50)||0)+(parseInt(sb.s75)||0)+(parseInt(sb.s100)||0) || 1;
  const sbPct = (v) => Math.round((parseInt(v)||0)/sbTotal*100);

  // Source breakdown (new: organic/direct/social/email/referral/internal)
  const srcRows  = stats.source_breakdown || [];
  const srcTotal = srcRows.reduce((s,r) => s + (parseInt(r.cnt)||0), 0) || 1;
  const srcColors = { organic:'#3b6fe0', direct:'#6b7280', social:'#ec4899', email:'#f59e0b', referral:'#8b5cf6', internal:'#f5c842' };
  const srcLabels = { organic:'🔍 Orgánico', direct:'🔗 Directo', social:'📱 Social', email:'📧 Email', referral:'🌐 Referral', internal:'🧭 Interno' };

  // A/B
  const abRows = stats.ab_results || [];
  const abA = abRows.find(r => r.ab_variant === 'A') || {};
  const abB = abRows.find(r => r.ab_variant === 'B') || {};
  const abWinner = (abA.cta_clicks||0) > 0 || (abB.cta_clicks||0) > 0
    ? ((abA.cta_clicks||0)/(abA.sessions||1)) > ((abB.cta_clicks||0)/(abB.sessions||1)) ? 'A' : 'B'
    : null;

  // Geo
  const geoCountries = stats.geo_countries || [];
  const geoMax = geoCountries[0]?.cnt || 1;

  // Hourly
  const hourMap = {};
  (stats.hourly||[]).forEach(r => { hourMap[r.hour_of_day] = parseInt(r.cnt)||0; });
  const maxHour = Math.max(1, ...Object.values(hourMap).length ? Object.values(hourMap) : [1]);

  // Daily
  const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  const dayMap = {};
  (stats.daily||[]).forEach(r => { dayMap[r.day_of_week] = parseInt(r.cnt)||0; });
  const maxDay = Math.max(1, ...Object.values(dayMap).length ? Object.values(dayMap) : [1]);

  // Referrer domains
  const refDomains = stats.referrer_domains || [];

  // Funnel
  const fTotal    = funnel?.total_completions || 0;
  const fFromBlog = funnel?.from_blog || 0;
  const fRate     = funnel?.conversion_rate || 0;
  const fLevels   = funnel?.levels || [];
  const fSlugs    = funnel?.top_slugs || [];
  const fLevelMax = fLevels[0]?.cnt || 1;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>

      {/* ── Export buttons ── */}
      <ExportButtons stats={stats} funnel={funnel} concurrent={concurrent} />

      {/* ── ROW 0b — Trend chart ── */}
      <Panel>
        <SectionTitle>Tendencia de visitas — últimos 30 días</SectionTitle>
        <TrendChart timeline={stats.timeline || []} />
      </Panel>

      {/* ── ROW 0 — Live concurrent ── */}
      {concurrent !== null && (
        <div style={{ background:'linear-gradient(135deg,#1a1744,#26225d)', borderRadius:'14px', padding:'16px 24px', display:'flex', alignItems:'center', gap:'16px' }}>
          <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#6ee7b7', flexShrink:0, animation:'pulse 1.5s ease-in-out infinite', boxShadow:'0 0 0 3px rgba(110,231,183,.2)' }}/>
          <div>
            <span style={{ fontFamily:'Fraunces,serif', fontSize:'1.4rem', fontWeight:900, color:'white' }}>{concurrent}</span>
            <span style={{ color:'rgba(255,255,255,.55)', fontSize:'0.85rem', marginLeft:'8px' }}>
              {concurrent === 1 ? 'persona en el sitio ahora mismo' : 'personas en el sitio ahora mismo'}
            </span>
          </div>
          <button onClick={() => window.location.reload()} style={{ marginLeft:'auto', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.6)', borderRadius:'8px', padding:'5px 12px', cursor:'pointer', fontSize:'0.75rem', fontFamily:'DM Sans,sans-serif' }}>
            Actualizar
          </button>
        </div>
      )}

      {/* ── ROW 1 — Core KPIs ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px' }}>
        <StatCard label="Vistas totales"    value={(total||0).toLocaleString()}          sub="todos los posts"              accent="#3b6fe0"/>
        <StatCard label="Sesiones únicas"   value={(sessions||0).toLocaleString()}        sub="con datos de engagement"      accent="#059669"/>
        <StatCard label="CTA click-rate"    value={`${ctrRate}%`}                         sub={`${eng.total_cta_clicks||0} clicks totales`} accent="#f5c842"/>
        <StatCard label="Tasa de rebote"    value={`${bounceRate}%`}                      sub="<10s y <10% scroll"           accent="#dc2626"/>
      </div>

      {/* ── ROW 2 — Engagement + WA ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px' }}>
        <StatCard label="Scroll promedio"   value={`${Math.round(eng.avg_scroll||0)}%`}  sub="profundidad de lectura"/>
        <StatCard label="Tiempo activo"     value={`${Math.round((eng.avg_time||0)/60*10)/10} min`} sub="promedio por sesión"/>
        <StatCard label="WA clicks"         value={(eng.total_wa_clicks||0).toLocaleString()} sub={`tasa ${waRate}%`} accent="#25d366"/>
        <StatCard label="WA leads"          value={(eng.total_wa_leads||0).toLocaleString()}  sub="abrieron WhatsApp" accent="#16a34a"/>
      </div>

      {/* ── ROW 3 — Funnel blog → examen → lead ── */}
      <Panel style={{ borderTop:'3px solid #f5c842' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
          <SectionTitle>Funnel blog → examen → lead</SectionTitle>
          {fRate > 0 && (
            <span style={{ background: fRate >= 5 ? '#f0fdf4' : '#fff7ed', color: fRate >= 5 ? '#166534' : '#9a3412', border: `1px solid ${fRate >= 5 ? '#86efac' : '#fed7aa'}`, borderRadius:'100px', padding:'3px 10px', fontSize:'0.72rem', fontWeight:700 }}>
              {fRate >= 5 ? `✅ ${fRate}% conversión` : `⚠️ ${fRate}% conversión`}
            </span>
          )}
        </div>

        {fTotal === 0 ? (
          <p style={{ color:'#999', fontSize:'0.85rem' }}>Sin datos de funnel aún. Asegúrate de que funnel-bridge.js esté instalado en linguahub.</p>
        ) : (
          <>
            {/* Funnel steps visual */}
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'20px', flexWrap:'wrap' }}>
              {[
                { label:'Sesiones blog', val: sessions, color:'#3b6fe0' },
                { label:'→ Llegaron al examen', val: fFromBlog, color:'#f59e0b' },
                { label:'→ Completaron examen', val: fTotal, color:'#22c55e' },
              ].map(({label,val,color},i) => (
                <React.Fragment key={i}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:'Fraunces,serif', fontSize:'1.6rem', fontWeight:900, color }}>{val.toLocaleString()}</div>
                    <div style={{ fontSize:'0.72rem', color:'#888', marginTop:'2px' }}>{label}</div>
                  </div>
                  {i < 2 && <div style={{ color:'#ddd', fontSize:'1.2rem', flexShrink:0 }}>›</div>}
                </React.Fragment>
              ))}
            </div>

            {/* Top posts que generan exámenes */}
            {fSlugs.length > 0 && (
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#aaa', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'8px' }}>Posts que más convierten a examen</div>
                {fSlugs.slice(0,5).map((s,i) => {
                  const pct = Math.round((s.cnt / fSlugs[0].cnt) * 100);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'6px' }}>
                      <div style={{ fontSize:'0.8rem', color:'var(--text)', flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.blog_slug || '(directo)'}</div>
                      <div style={{ width:'100px', height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden', flexShrink:0 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:'#f5c842', borderRadius:'3px' }}/>
                      </div>
                      <span style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--navy)', width:'20px', textAlign:'right', flexShrink:0 }}>{s.cnt}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Levels distribution */}
            {fLevels.length > 0 && (
              <div>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#aaa', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'8px' }}>Distribución de niveles obtenidos</div>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                  {fLevels.map((l,i) => {
                    const pct = Math.round((l.cnt / fLevelMax) * 100);
                    const col = ['#059669','#3b6fe0','#7c3aed','#ea580c','#dc2626'][i % 5];
                    return (
                      <div key={i} style={{ background:'#f8f9fa', border:'1px solid #e5e7eb', borderRadius:'8px', padding:'8px 12px', textAlign:'center', minWidth:'60px' }}>
                        <div style={{ fontFamily:'Fraunces,serif', fontSize:'1rem', fontWeight:900, color:col }}>{l.cnt}</div>
                        <div style={{ fontSize:'0.68rem', color:'#888', marginTop:'2px' }}>{l.level}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </Panel>

      {/* ── ROW 4 — A/B Results ── */}
      <Panel>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <SectionTitle>A/B Test — variantes del CTA</SectionTitle>
          {abWinner && <span style={{ background:'#f0fdf4', color:'#166534', border:'1px solid #86efac', borderRadius:'100px', padding:'3px 10px', fontSize:'0.72rem', fontWeight:700 }}>Ganador: Variante {abWinner}</span>}
        </div>
        {abRows.length === 0 ? (
          <p style={{ color:'#999', fontSize:'0.85rem' }}>Sin datos A/B aún.</p>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
            {[['A', abA], ['B', abB]].map(([variant, d]) => {
              const isWinner = abWinner === variant;
              const ctaRate = d.sessions > 0 ? ((d.cta_clicks||0)/(d.sessions||1)*100).toFixed(1) : '0';
              const waLeadRate = d.sessions > 0 ? ((d.wa_leads||0)/(d.sessions||1)*100).toFixed(1) : '0';
              return (
                <div key={variant} style={{ background: isWinner ? '#f0fdf4' : '#fafaf9', border:`1.5px solid ${isWinner ? '#86efac' : '#e5e7eb'}`, borderRadius:'12px', padding:'16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                    <span style={{ fontFamily:'Fraunces,serif', fontWeight:900, color:'var(--navy)', fontSize:'1.1rem' }}>Variante {variant}</span>
                    {isWinner && <span style={{ fontSize:'0.72rem', color:'#166534', fontWeight:700 }}>✓ Ganador</span>}
                  </div>
                  {[
                    ['Sesiones',    (d.sessions||0).toLocaleString(), null],
                    ['CTA rate',    `${ctaRate}%`,                    ctaRate >= 3 ? '#059669' : '#dc2626'],
                    ['WA clicks',   (d.wa_clicks||0).toLocaleString(), null],
                    ['WA leads',    `${waLeadRate}%`,                 waLeadRate >= 2 ? '#059669' : null],
                    ['Scroll avg',  `${Math.round(d.avg_scroll||0)}%`, null],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                      <span style={{ fontSize:'0.8rem', color:'#888' }}>{lbl}</span>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color: color || 'var(--navy)' }}>{val}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* ── ROW 5 — Source breakdown + Device ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <Panel>
          <SectionTitle>Fuente de tráfico</SectionTitle>
          {srcRows.length === 0 ? <p style={{ color:'#999', fontSize:'0.85rem' }}>Sin datos.</p> : (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {srcRows.map((s,i) => {
                const pct = Math.round((s.cnt/srcTotal)*100);
                const col = srcColors[s.source] || '#888';
                const lbl = srcLabels[s.source] || s.source;
                return (
                  <div key={i}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
                      <span style={{ fontSize:'0.82rem', color:'#555' }}>{lbl}</span>
                      <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--navy)' }}>{pct}% <span style={{ color:'#aaa', fontWeight:400 }}>({s.cnt})</span></span>
                    </div>
                    <div style={{ height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:col, borderRadius:'3px', transition:'width .4s' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel>
          <SectionTitle>Dispositivo</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginTop:'4px' }}>
            {[['📱 Móvil', devPct.mobile, '#3b6fe0', eng.mobile_count||0],
              ['🖥️ Desktop', devPct.desktop, '#059669', eng.desktop_count||0],
              ['📐 Tablet', devPct.tablet, '#f5c842', eng.tablet_count||0]].map(([l,pct,c,n]) => (
              <div key={l}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                  <span style={{ fontSize:'0.82rem', color:'#555' }}>{l}</span>
                  <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--navy)' }}>{pct}% <span style={{ color:'#aaa', fontWeight:400 }}>({n})</span></span>
                </div>
                <div style={{ height:'6px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:c, borderRadius:'3px', transition:'width .4s' }}/>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* ── ROW 6 — Geo países ── */}
      {/* ── ROW 6 — Mapa de ciudades + países ── */}
      {(geoCountries.length > 0 || stats.geo_cities?.length > 0) && (
        <Panel>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <SectionTitle>Origen geográfico</SectionTitle>
            <div style={{ display:'flex', gap:'12px', fontSize:'0.72rem', color:'#aaa' }}>
              <span style={{ display:'flex', alignItems:'center', gap:'4px' }}><span style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#3b6fe0', display:'inline-block' }}/>México</span>
              <span style={{ display:'flex', alignItems:'center', gap:'4px' }}><span style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#f59e0b', display:'inline-block' }}/>Internacional</span>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 200px', gap:'20px', alignItems:'start' }}>

            {/* MAP SVG */}
            <GeoMap cities={stats.geo_cities || []} />

            {/* Countries list */}
            <div>
              <div style={{ fontSize:'0.68rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em', color:'#bbb', marginBottom:'10px' }}>Países</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
                {geoCountries.slice(0,8).map((g,i) => {
                  const pct = Math.round((g.cnt / geoMax) * 100);
                  return (
                    <div key={i}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
                        <span style={{ fontSize:'0.78rem', color:'#555', display:'flex', alignItems:'center', gap:'5px' }}>
                          {g.country_code && <span style={{ background:'#f0ece4', borderRadius:'3px', padding:'1px 4px', fontSize:'0.62rem', fontWeight:700, color:'#888' }}>{g.country_code}</span>}
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100px' }}>{g.country_name}</span>
                        </span>
                        <span style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--navy)', flexShrink:0 }}>{g.cnt}</span>
                      </div>
                      <div style={{ height:'4px', background:'#f0ece4', borderRadius:'2px', overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%', background: i===0?'#3b6fe0':'#93c5fd', borderRadius:'2px' }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Panel>
      )}

      {/* ── ROW 7 — Scroll depth ── */}
      <Panel>
        <SectionTitle>Profundidad de lectura — ¿hasta dónde leen?</SectionTitle>
        <div style={{ display:'flex', alignItems:'flex-end', gap:'8px', height:'80px' }}>
          {[['0–24%','s0','#fecaca'],['25–49%','s25','#fed7aa'],['50–74%','s50','#fde68a'],['75–99%','s75','#a7f3d0'],['100%','s100','#6ee7b7']].map(([lbl,key,color]) => {
            const pct = sbPct(sb[key]);
            return (
              <div key={key} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                <span style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--navy)' }}>{pct}%</span>
                <div style={{ width:'100%', height:`${Math.max(4,pct)}%`, background:color, borderRadius:'4px 4px 0 0', transition:'height .5s', minHeight:'4px' }}/>
                <span style={{ fontSize:'0.68rem', color:'#aaa', textAlign:'center' }}>{lbl}</span>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop:'12px', fontSize:'0.75rem', color:'#888' }}>
          {sbPct(sb.s100) >= 30 ? `✅ ${sbPct(sb.s100)}% llegan al final — buen contenido.`
            : sbPct(sb.s0) >= 50 ? `⚠️ ${sbPct(sb.s0)}% se van en la primera cuarta parte — revisa el intro.`
            : `📊 ${sbPct(sb.s50)}% pasa de la mitad del artículo.`}
        </div>
      </Panel>

      {/* ── ROW 8 — Referrer domains + UTM ── */}
      {(refDomains.length > 0 || (stats.utm_sources||[]).length > 0) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          {refDomains.length > 0 && (
            <Panel>
              <SectionTitle>Dominios referidores</SectionTitle>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {refDomains.slice(0,8).map((r,i) => {
                  const pct = Math.round((r.cnt / refDomains[0].cnt) * 100);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <span style={{ fontSize:'0.8rem', color:'#555', flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.referrer_domain}</span>
                      <div style={{ width:'80px', height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden', flexShrink:0 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:'#8b5cf6', borderRadius:'3px' }}/>
                      </div>
                      <span style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--navy)', width:'22px', textAlign:'right', flexShrink:0 }}>{r.cnt}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
          {(stats.utm_sources||[]).length > 0 && (
            <Panel>
              <SectionTitle>Fuentes UTM</SectionTitle>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {(stats.utm_sources||[]).map((u,i) => {
                  const maxUTM = stats.utm_sources[0]?.cnt || 1;
                  const pct = Math.round((u.cnt/maxUTM)*100);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <span style={{ fontSize:'0.8rem', color:'#555', flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.utm_source}</span>
                      <div style={{ width:'80px', height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden', flexShrink:0 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:'#7c3aed', borderRadius:'3px' }}/>
                      </div>
                      <span style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--navy)', width:'22px', textAlign:'right', flexShrink:0 }}>{u.cnt}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ── ROW 9 — Horas + Días ── */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'14px' }}>
        <Panel>
          <SectionTitle>Horas del día con más lecturas (hora MX)</SectionTitle>
          <div style={{ display:'flex', alignItems:'flex-end', gap:'2px', height:'60px', marginBottom:'6px' }}>
            {Array.from({length:24},(_,h) => {
              const cnt = hourMap[h]||0;
              const pct = Math.round((cnt/maxHour)*100);
              return (
                <div key={h} title={`${h}:00 — ${cnt} visitas`}
                  style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'flex-end', height:'100%', cursor:'default' }}>
                  <div style={{ width:'100%', height:`${Math.max(2,pct)}%`, background: pct>=70?'#3b6fe0': pct>=30?'#93c5fd':'#dbeafe', borderRadius:'2px 2px 0 0', minHeight:'2px' }}/>
                </div>
              );
            })}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            {['12am','6am','12pm','6pm','11pm'].map(l => <span key={l} style={{ fontSize:'0.65rem', color:'#aaa' }}>{l}</span>)}
          </div>
          {Object.keys(hourMap).length > 0 && (() => {
            const peakH = Object.entries(hourMap).sort((a,b)=>b[1]-a[1])[0];
            return <p style={{ fontSize:'0.75rem', color:'#888', marginTop:'8px' }}>Pico: {peakH[0]}:00 h con {peakH[1]} visitas</p>;
          })()}
        </Panel>
        <Panel>
          <SectionTitle>Día de la semana</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {DAY_NAMES.map((d,i) => {
              const cnt = dayMap[i]||0;
              const pct = Math.round((cnt/maxDay)*100);
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ width:'28px', fontSize:'0.75rem', color:'#888', flexShrink:0 }}>{d}</span>
                  <div style={{ flex:1, height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background: i===0||i===6?'#f5c842':'#3b6fe0', borderRadius:'3px' }}/>
                  </div>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--navy)', width:'24px', textAlign:'right', flexShrink:0 }}>{cnt}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* ── ROW 10 — Posts rendimiento ── */}
      <Panel>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <SectionTitle>Rendimiento por artículo</SectionTitle>
          <div style={{ display:'flex', gap:'12px' }}>
            {[['#3b6fe0','Orgánico'],['#f5c842','Interno'],['#25d366','WA leads']].map(([c,l])=>(
              <span key={l} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.72rem', color:'#888', fontWeight:600 }}>
                <span style={{ width:'8px', height:'8px', borderRadius:'2px', background:c, display:'inline-block' }}/>{l}
              </span>
            ))}
          </div>
        </div>
        {(stats.posts||[]).length === 0 && <p style={{ color:'#999', fontSize:'0.875rem' }}>Sin datos aún.</p>}
        {(stats.posts||[]).map((p,i) => {
          const max     = stats.posts[0]?.views || 1;
          const totPct  = Math.round(((p.views||0)/max)*100);
          const orgPct2 = Math.round(((p.views_organic||0)/max)*100);
          const intPct2 = Math.round(((p.views_internal||0)/max)*100);
          const hasEng  = p.avg_scroll != null;
          const hasSrc  = (p.views_organic||0)+(p.views_internal||0) > 0;
          return (
            <div key={i} style={{ marginBottom:'20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}>
                <div style={{ width:'4px', height:'14px', background:p.category_color||'#3b6fe0', borderRadius:'2px', flexShrink:0 }}/>
                <div style={{ flex:1, fontSize:'0.85rem', color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title}</div>
                <div style={{ display:'flex', gap:'10px', flexShrink:0, alignItems:'center' }}>
                  {hasEng && <>
                    <span style={{ fontSize:'0.7rem', color:'#888' }} title="Scroll promedio">📜 {p.avg_scroll}%</span>
                    <span style={{ fontSize:'0.7rem', color:'#888' }} title="Tiempo activo">⏱ {Math.round((p.avg_time||0)/60*10)/10}m</span>
                    {(p.cta_clicks||0)>0 && <span style={{ fontSize:'0.7rem', color:'#059669', fontWeight:700 }}>🎯 {p.cta_clicks}</span>}
                    {(p.wa_leads||0)>0 && <span style={{ fontSize:'0.7rem', color:'#16a34a', fontWeight:700 }}>💬 {p.wa_leads}</span>}
                  </>}
                  {hasSrc && <>
                    <span style={{ fontSize:'0.73rem', color:'#3b6fe0', fontWeight:700 }}>{p.views_organic||0}</span>
                    <span style={{ color:'#ddd', fontSize:'0.7rem' }}>|</span>
                    <span style={{ fontSize:'0.73rem', color:'#b8860b', fontWeight:700 }}>{p.views_internal||0}</span>
                    <span style={{ color:'#ddd', fontSize:'0.7rem' }}>|</span>
                  </>}
                  <span style={{ fontSize:'0.85rem', fontWeight:700, color:'var(--navy)', width:'32px', textAlign:'right' }}>{p.views}</span>
                </div>
              </div>
              <div style={{ height:'6px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden', display:'flex' }}>
                {hasSrc
                  ? <><div style={{ width:`${orgPct2}%`, height:'100%', background:'#3b6fe0' }}/><div style={{ width:`${intPct2}%`, height:'100%', background:'#f5c842' }}/></>
                  : <div style={{ width:`${totPct}%`, height:'100%', background:p.category_color||'#3b6fe0', borderRadius:'3px' }}/>
                }
              </div>
              {hasEng && (p.bounced||0)>0 && (p.sessions||0)>0 && (
                <div style={{ fontSize:'0.68rem', color:'#aaa', marginTop:'3px' }}>
                  Rebote: {Math.round((p.bounced/p.sessions)*100)}% de {p.sessions} sesiones
                </div>
              )}
            </div>
          );
        })}
      </Panel>

      {/* ── ROW 11 — Summary ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <StatCard label="Top categoría"     value={stats.top_cat?.category||'—'} sub={`${(stats.top_cat?.total||0).toLocaleString()} vistas acumuladas`}/>
        <StatCard label="Promedio por post" value={stats.avg||0}                  sub="vistas por artículo publicado"/>
      </div>

    </div>
  );
}


// ── Main Admin ──────────────────────────────────────────────────
export default function BlogAdmin() {
  const [authed,  setAuthed]  = useState(!!getToken());
  const [posts,   setPosts]   = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab,     setTab]     = useState('articulos');
  const [stats,   setStats]   = useState(null);
  const [funnel,  setFunnel]  = useState(null);
  const [concurrent, setConcurrent] = useState(null);

  const loadStats = () => {
    fetch(`${API}?action=stats`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setStats(d)).catch(()=>{});
    fetch(`${API}?action=funnel_stats`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setFunnel(d)).catch(()=>{});
    fetch(`${API}?action=concurrent`)
      .then(r => r.json()).then(d => setConcurrent(d.concurrent ?? null)).catch(()=>{});
  };
  const loadPosts = () => {
    setLoading(true);
    fetch(`${API}?action=admin_posts`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setPosts(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { if (authed) { loadPosts(); loadStats(); } }, [authed]);

  const editPost = async (id) => {
    const r = await fetch(`${API}?action=admin_post&id=${id}`, { headers: authHeaders() });
    setEditing(await r.json());
  };
  const deletePost = async (id, title) => {
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    await fetch(`${API}?action=delete&id=${id}`, { method: 'DELETE', headers: authHeaders() });
    loadPosts();
  };
  const togglePublished = async (post) => {
    await fetch(`${API}?action=update&id=${post.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ ...post, published: post.published ? 0 : 1 }) });
    loadPosts();
  };
  const logout = () => { localStorage.removeItem('blog_token'); setAuthed(false); };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  // ── Topbar shared ──
  const Topbar = ({ extra }) => (
    <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 32px', height: '52px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 700, color: 'var(--navy)', fontSize: '1rem' }}>Blog Admin · Culturlingua</span>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        {extra}
        <a href="/blog" target="_blank" style={{ color: '#999', fontSize: '0.85rem', textDecoration: 'none' }}>Ver blog →</a>
        <button onClick={logout}
          style={{ padding: '6px 14px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: 'white', color: '#666', fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600 }}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  if (editing !== null) return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingTop: NAV_H }}>
      <Topbar extra={<button onClick={() => setEditing(null)} style={{ background:'none', border:'none', color:'#999', cursor:'pointer', fontSize:'0.85rem' }}>← Volver</button>} />
      <Editor post={editing} onSave={() => { setEditing(null); loadPosts(); }} onCancel={() => setEditing(null)} />
    </div>
  );

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingTop: NAV_H }}>
      <Topbar />

      <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', borderBottom: '1.5px solid var(--border)', marginBottom: '28px' }}>
          {[['articulos','📝 Artículos'],['analytics','📊 Analytics']].map(([t,lbl]) => (
            <button key={t} onClick={() => { setTab(t); if (t==='analytics') loadStats(); }}
              style={{ padding: '10px 22px', borderRadius: '10px 10px 0 0', border: '1.5px solid', borderBottom: 'none',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', marginBottom: '-1.5px',
                background: tab===t ? 'white' : 'transparent',
                borderColor: tab===t ? 'var(--border)' : 'transparent',
                color: tab===t ? 'var(--navy)' : 'var(--muted)' }}>
              {lbl}
            </button>
          ))}
        </div>

        {/* ── Tab: Artículos ── */}
        {tab==='articulos' && (<>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'28px' }}>
            <div>
              <h1 style={{ fontFamily:'Fraunces, serif', color:'var(--navy)', fontSize:'1.8rem', marginBottom:'4px' }}>Artículos</h1>
              <p style={{ color:'#999', fontSize:'0.875rem' }}>{posts.length} artículo{posts.length!==1?'s':''} en total</p>
            </div>
            <button onClick={() => setEditing({})}
              style={{ padding:'12px 24px', borderRadius:'10px', background:'var(--navy)', color:'white', border:'none', fontWeight:700, fontFamily:'DM Sans, sans-serif', cursor:'pointer', fontSize:'0.9rem' }}>
              + Nuevo artículo
            </button>
          </div>

          {loading && <p style={{ color:'#999', textAlign:'center', padding:'40px' }}>Cargando...</p>}
          {!loading && posts.length===0 && (
            <div style={{ textAlign:'center', padding:'80px', background:'white', borderRadius:'16px' }}>
              <p style={{ fontSize:'2.5rem' }}>📝</p>
              <h3 style={{ fontFamily:'Fraunces, serif', color:'var(--navy)', marginTop:'12px' }}>Sin artículos aún</h3>
              <p style={{ color:'#999', marginTop:'8px' }}>Haz clic en "+ Nuevo artículo" para empezar.</p>
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {posts.map(post => (
              <div key={post.id} style={{ background:'white', borderRadius:'14px', padding:'16px 20px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', display:'flex', alignItems:'center', gap:'14px' }}>
                <div style={{ width:'4px', height:'44px', background:post.category_color||'#3b6fe0', borderRadius:'2px', flexShrink:0 }}/>
                <div style={{ width:'38px', height:'38px', borderRadius:'9px', background:(post.category_color||'#3b6fe0')+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:post.icon?'20px':'13px', flexShrink:0, color:post.category_color||'#3b6fe0', fontWeight:800 }}>
                  {post.icon||(post.category||'G').substring(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                    <span style={{ fontFamily:'Fraunces, serif', fontWeight:700, color:'var(--navy)', fontSize:'0.97rem' }}>{post.title}</span>
                    <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:'100px', fontWeight:700, background:post.published?'#d1fae5':'#f3f4f6', color:post.published?'#065f46':'#6b7280' }}>
                      {post.published?'Publicado':'Borrador'}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>{post.category}</span>
                    <span style={{ color:'#ddd' }}>·</span>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>/blog/{post.slug}</span>
                    <span style={{ color:'#ddd' }}>·</span>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>{post.read_time}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:'7px', flexShrink:0 }}>
                  <button onClick={() => togglePublished(post)}
                    style={{ padding:'6px 12px', borderRadius:'8px', border:'1.5px solid #e5e7eb', background:'white', color:'#666', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    {post.published?'Ocultar':'Publicar'}
                  </button>
                  <button onClick={() => editPost(post.id)}
                    style={{ padding:'6px 14px', borderRadius:'8px', border:'none', background:'var(--navy)', color:'white', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    Editar
                  </button>
                  <button onClick={() => deletePost(post.id, post.title)}
                    style={{ padding:'6px 12px', borderRadius:'8px', border:'1.5px solid #fecaca', background:'white', color:'#dc2626', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>)}

        {/* ── Tab: Analytics ── */}
        {tab==='analytics' && <AnalyticsTab stats={stats} funnel={funnel} concurrent={concurrent} />}

      </div>
    </div>
  );
}